import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { GetStartedButton } from './GetStartedButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { LogOut, Upload, History, ReceiptText, Settings } from 'lucide-react';

export interface SiteHeaderProps {
  isAuthenticated?: boolean;
  userName?: string;
  onSignOut?: () => void;
}

/** Fixed dark-navy header used across the V3 site template. Defaults to the
 *  public-marketing variant (Sign in + Get Started). Pass `isAuthenticated`
 *  to render the in-app variant (Upload + History + user menu).
 */
export function SiteHeader({ isAuthenticated = false, userName, onSignOut }: SiteHeaderProps = {}) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const userInitials = userName
    ? userName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'U';

  return (
    <header className="on-navy fixed inset-x-0 top-0 z-50 border-b border-white/15 bg-[#000b57]/95 backdrop-blur-xl">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-6">
        <Link to={isAuthenticated ? '/upload' : '/'} className="flex items-center gap-2 text-white">
          <Logo size={22} />
          <span className="text-lg font-bold tracking-tight">socialyse.ai</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-4 md:flex">
          {isAuthenticated ? (
            <>
              <Link
                to="/upload"
                className="text-sm font-medium text-white/80 hover:text-white transition-colors"
              >
                Upload
              </Link>
              {/* #163 — "History" is two views now, so the top-nav item is a
                  menu rather than a link. It also resolves the old label
                  mismatch: this said "History" while the account dropdown said
                  "Analysis History" and both went to the same route. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="text-sm font-medium text-white/80 hover:text-white transition-colors"
                  >
                    History
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/history" className="cursor-pointer">
                      <History className="mr-2 h-4 w-4" />
                      <span>Analysis History</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/orders" className="cursor-pointer">
                      <ReceiptText className="mr-2 h-4 w-4" />
                      <span>Order History</span>
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label="Open account menu"
                    className="relative h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center"
                  >
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-white/10 text-white text-sm">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      {userName && <p className="font-medium">{userName}</p>}
                      <p className="text-xs text-muted-foreground">Manage your account</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/upload" className="cursor-pointer">
                      <Upload className="mr-2 h-4 w-4" />
                      <span>Upload Data</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/history" className="cursor-pointer">
                      <History className="mr-2 h-4 w-4" />
                      <span>Analysis History</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/orders" className="cursor-pointer">
                      <ReceiptText className="mr-2 h-4 w-4" />
                      <span>Order History</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="cursor-pointer">
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Profile Settings</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={onSignOut}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link
                to="/pricing"
                className="text-sm font-medium text-white/80 hover:text-white transition-colors"
              >
                Pricing
              </Link>
              <Link
                to="/demo/dashboard"
                className="text-sm font-medium text-white/80 hover:text-white transition-colors"
              >
                View Demo
              </Link>
              <Link
                to="/sign-in"
                className="inline-flex items-center justify-center rounded-[4px] bg-white/10 px-4 py-2.5 text-[15px] font-semibold tracking-[-0.15px] text-white hover:bg-white/20 transition-colors"
              >
                Sign in
              </Link>
              <GetStartedButton />
            </>
          )}
        </nav>

        {/* Mobile hamburger */}
        <button
          type="button"
          className="md:hidden text-white p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
        >
          <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2">
            {mobileOpen ? <path d="M6 6l12 12M6 18L18 6" /> : <path d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 bg-[#000b57] px-6 pb-6 pt-4">
          <div className="flex flex-col gap-3">
            {isAuthenticated ? (
              <>
                <Link to="/upload" className="text-sm font-medium text-white/80 py-2">
                  Upload
                </Link>
                <Link to="/history" className="text-sm font-medium text-white/80 py-2">
                  Analysis History
                </Link>
                <Link to="/orders" className="text-sm font-medium text-white/80 py-2">
                  Order History
                </Link>
                <Link to="/profile" className="text-sm font-medium text-white/80 py-2">
                  Profile
                </Link>
                <button
                  type="button"
                  onClick={onSignOut}
                  className="rounded-[4px] bg-white/10 px-4 py-2.5 text-sm font-medium text-white text-left"
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/pricing" className="text-sm font-medium text-white/80 py-2">
                  Pricing
                </Link>
                <Link to="/demo/dashboard" className="text-sm font-medium text-white/80 py-2">
                  View Demo
                </Link>
                <Link
                  to="/sign-in"
                  className="rounded-[4px] bg-white/10 px-4 py-2.5 text-sm font-medium text-white text-center"
                >
                  Sign in
                </Link>
                <GetStartedButton className="justify-center" />
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
